import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import theme from './theme/theme';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import PagePlaceholder from './components/PagePlaceholder';

import MainLayout from './layouts/MainLayout';

// Auth pages
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import ChangePassword from './pages/auth/ChangePassword';

// Dashboard
import Dashboard from './pages/dashboard/Dashboard';

// Empresas (Fase 3)
import EmpresasList from './pages/empresas/EmpresasList';
import EmpresaForm from './pages/empresas/EmpresaForm';
import EmpresaDetail from './pages/empresas/EmpresaDetail';
import MiEmpresa from './pages/empresas/MiEmpresa';

// Afiliados (Fase 3)
import AfiliadosList from './pages/afiliados/AfiliadosList';
import AfiliadoForm from './pages/afiliados/AfiliadoForm';

// Trámites de afiliados (buzón del gremio)
import BandejaTramites from './pages/tramites/BandejaTramites';

// Usuarios (Fase 2)
import UsuariosList from './pages/usuarios/UsuariosList';

// Configuracion (admin)
import ConceptosAporteList from './pages/configuracion/ConceptosAporteList';
import ParametrosList from './pages/configuracion/ParametrosList';
import CategoriasList from './pages/configuracion/catalogos/CategoriasList';
import ProvinciasList from './pages/configuracion/catalogos/ProvinciasList';
import LocalidadesList from './pages/configuracion/catalogos/LocalidadesList';
import ParentescosList from './pages/configuracion/catalogos/ParentescosList';
import AuditoriaList from './pages/configuracion/AuditoriaList';

// Periodos (Fase 4)
import PeriodosList from './pages/periodos/PeriodosList';

// DDJJ (Fase 4.3)
import DDJJList from './pages/ddjj/DDJJList';
import DDJJDetalle from './pages/ddjj/DDJJDetalle';

// Cupones (Fase 4.4)
import CuponesList from './pages/cupones/CuponesList';
import CuponDetalle from './pages/cupones/CuponDetalle';

// Pagos (Fase 5)
import PagosList from './pages/pagos/PagosList';

// CVUs (Fase 6.1)
import CvusList from './pages/cvus/CvusList';

// Transacciones (Fase 6.2)
import TransaccionesList from './pages/transacciones/TransaccionesList';

// Cuenta Corriente (Fase 7.1)
import CuentaCorrienteList from './pages/cuenta-corriente/CuentaCorrienteList';
import CuentaCorrienteDetalle from './pages/cuenta-corriente/CuentaCorrienteDetalle';
import MiCuentaCorriente from './pages/cuenta-corriente/MiCuentaCorriente';

// Reportes (Fase 7.3)
import ReportesHome from './pages/reportes/ReportesHome';
import CuponesNoPagadosReporte from './pages/reportes/CuponesNoPagadosReporte';
import CuentaCorrienteReporte from './pages/reportes/CuentaCorrienteReporte';
import DeudaPorEmpresaReporte from './pages/reportes/DeudaPorEmpresaReporte';
import RecaudacionPorPeriodoReporte from './pages/reportes/RecaudacionPorPeriodoReporte';
import PersonalEmpresaReporte from './pages/reportes/PersonalEmpresaReporte';
import DetalleDDJJReporte from './pages/reportes/DetalleDDJJReporte';
import MovimientoRelacionesReporte from './pages/reportes/MovimientoRelacionesReporte';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Auth (publicas) */}
              <Route path="/auth/login" element={<Login />} />
              <Route path="/auth/forgot-password" element={<ForgotPassword />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />

              {/* App (protegidas) */}
              <Route
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />

                {/* Fase 3 — Empresas */}
                <Route path="empresas" element={<EmpresasList />} />
                <Route path="empresas/nuevo" element={<EmpresaForm />} />
                <Route path="empresas/:id" element={<EmpresaDetail />} />
                <Route path="empresas/:id/editar" element={<EmpresaForm />} />
                <Route path="mi-empresa" element={<MiEmpresa />} />

                {/* Afiliados */}
                <Route path="afiliados" element={<AfiliadosList />} />
                <Route path="afiliados/nuevo" element={<AfiliadoForm />} />
                <Route path="afiliados/:id" element={<AfiliadoForm />} />
                <Route path="afiliados/:id/editar" element={<AfiliadoForm />} />

                {/* Buzón de trámites de afiliados (solo gremio) */}
                <Route
                  path="tramites"
                  element={
                    <ProtectedRoute roles={['admin_general', 'operador']}>
                      <BandejaTramites />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tramites/:id"
                  element={
                    <ProtectedRoute roles={['admin_general', 'operador']}>
                      <BandejaTramites />
                    </ProtectedRoute>
                  }
                />

                {/* Periodos */}
                <Route
                  path="periodos"
                  element={
                    <ProtectedRoute roles={['admin_general', 'operador']}>
                      <PeriodosList />
                    </ProtectedRoute>
                  }
                />

                {/* Fase 4 — DDJJ */}
                <Route path="ddjj" element={<DDJJList />} />
                <Route path="ddjj/:id" element={<DDJJDetalle />} />

                {/* Fase 4.4 — Cupones */}
                <Route path="cupones" element={<CuponesList />} />
                <Route path="cupones/:id" element={<CuponDetalle />} />

                {/* Fase 5 — Pagos */}
                <Route path="pagos" element={<PagosList />} />
                <Route
                  path="acreditacion"
                  element={<PagePlaceholder title="Acreditacion Automatica" icon="upload_file" phase="Fase 5" />}
                />

                {/* Fase 6 — Pronto Pago */}
                <Route
                  path="cvus"
                  element={
                    <ProtectedRoute roles={['admin_general']}>
                      <CvusList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="transacciones"
                  element={
                    <ProtectedRoute roles={['admin_general', 'operador']}>
                      <TransaccionesList />
                    </ProtectedRoute>
                  }
                />

                {/* Fase 7 — Cuenta Corriente / Reportes */}
                <Route path="cuenta-corriente" element={<CuentaCorrienteList />} />
                <Route
                  path="cuenta-corriente/:empresaId"
                  element={
                    <ProtectedRoute roles={['admin_general', 'operador']}>
                      <CuentaCorrienteDetalle />
                    </ProtectedRoute>
                  }
                />
                <Route path="mi-cuenta-corriente" element={<MiCuentaCorriente />} />
                <Route
                  path="reportes"
                  element={
                    <ProtectedRoute roles={['admin_general', 'operador']}>
                      <ReportesHome />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="reportes/cupones-no-pagados"
                  element={
                    <ProtectedRoute roles={['admin_general', 'operador']}>
                      <CuponesNoPagadosReporte />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="reportes/cuenta-corriente"
                  element={
                    <ProtectedRoute roles={['admin_general', 'operador']}>
                      <CuentaCorrienteReporte />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="reportes/deuda-por-empresa"
                  element={
                    <ProtectedRoute roles={['admin_general', 'operador']}>
                      <DeudaPorEmpresaReporte />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="reportes/recaudacion-por-periodo"
                  element={
                    <ProtectedRoute roles={['admin_general', 'operador']}>
                      <RecaudacionPorPeriodoReporte />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="reportes/personal-empresa"
                  element={
                    <ProtectedRoute roles={['admin_general', 'operador']}>
                      <PersonalEmpresaReporte />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="reportes/detalle-ddjj"
                  element={
                    <ProtectedRoute roles={['admin_general', 'operador']}>
                      <DetalleDDJJReporte />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="reportes/movimiento-relaciones"
                  element={
                    <ProtectedRoute roles={['admin_general', 'operador']}>
                      <MovimientoRelacionesReporte />
                    </ProtectedRoute>
                  }
                />

                {/* Fase 2 — Usuarios */}
                <Route
                  path="usuarios"
                  element={
                    <ProtectedRoute roles={['admin_general']}>
                      <UsuariosList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="configuracion"
                  element={
                    <ProtectedRoute roles={['admin_general']}>
                      <ParametrosList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="configuracion/parametros"
                  element={
                    <ProtectedRoute roles={['admin_general']}>
                      <ParametrosList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="configuracion/conceptos-aporte"
                  element={
                    <ProtectedRoute roles={['admin_general']}>
                      <ConceptosAporteList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="configuracion/catalogos/categorias"
                  element={
                    <ProtectedRoute roles={['admin_general']}>
                      <CategoriasList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="configuracion/catalogos/provincias"
                  element={
                    <ProtectedRoute roles={['admin_general']}>
                      <ProvinciasList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="configuracion/catalogos/localidades"
                  element={
                    <ProtectedRoute roles={['admin_general']}>
                      <LocalidadesList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="configuracion/catalogos/parentescos"
                  element={
                    <ProtectedRoute roles={['admin_general']}>
                      <ParentescosList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="configuracion/auditoria"
                  element={
                    <ProtectedRoute roles={['admin_general']}>
                      <AuditoriaList />
                    </ProtectedRoute>
                  }
                />

                <Route path="cambiar-contrasena" element={<ChangePassword />} />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
